// src\themes\mad\elements\Header\HeaderNavSearch.tsx
"use client";
import { useState, useRef, useEffect } from "react";
import { Clock, Search, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Button } from "@/components/ui/button";

export function HeaderNavSearch({ scrolled }: { scrolled: boolean }) {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();
    const { dictionary } = useDictionary();

    // load recent searches from localStorage
    useEffect(() => {
        const saved = localStorage.getItem("recentSearches");
        if (saved) {
            try {
                setRecentSearches(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse recent searches:", e);
            }
        }
    }, []);

    // focus input when modal opens
    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    // close modal with ESC
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            return () => document.removeEventListener("keydown", handleEscape);
        }
    }, [isOpen]);

    const handleSearch = async (searchQuery: string) => {
        if (!searchQuery.trim()) return;
        setIsLoading(true);
        try {
            const updated = [searchQuery, ...recentSearches.filter((s) => s !== searchQuery)].slice(0, 5);
            setRecentSearches(updated);
            localStorage.setItem("recentSearches", JSON.stringify(updated));

            router.push(`/shop/category/products/all?search=${encodeURIComponent(searchQuery)}`);
            setIsOpen(false);
            setQuery("");
        } catch (error) {
            console.error("Search error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSearch(query);
    };

    const handleRecentClick = (search: string) => {
        setQuery(search);
        handleSearch(search);
    };

    return (
        <>
            <Button
                onClick={() => setIsOpen(true)}
                variant="ghost"
                size="icon"
                aria-label="Open search"
                className={`flex items-center justify-center p-2 rounded-full backdrop-blur-md
          ${scrolled
                        ? "rounded-full border border-primary-200"
                        : "bg-primary-400/30 hover:bg-primary-500/50"}`
                }
            >
                <Search className="h-5 w-5 stroke-1" />
            </Button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-primary-900/30 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Modal */}
                    <div className="relative w-full bg-card animate-in fade-in slide-in-from-top-4 p-4 duration-200">
                        {/* Search Bar */}
                        <form onSubmit={handleSubmit} className="relative">
                            <div className="flex items-center border-b-2">
                                <div className="flex items-center justify-center">
                                    <Search className="h-5 w-5 stroke-1" />
                                </div>

                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={dictionary.ui.product.searchProducts}
                                    className="w-full px-4 py-4 text-foreground placeholder:text-primary-600 focus:outline-none"
                                />

                                {isLoading && (
                                    <div className="mr-3">
                                        <div className="h-4 w-4 animate-spin border-2 border-accent border-t-transparent rounded-full" />
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsOpen(false);
                                        setQuery("");
                                    }}
                                    className="mr-2 p-2 text-primary-600 transition-colors hover:bg-muted hover:text-foreground"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </form>

                        {/* Recent Searches */}
                        {recentSearches.length > 0 && (
                            <div className="max-h-96 overflow-y-auto p-2">
                                <div className="mb-2 px-3 py-2 text-xs tracking-wide text-primary-600">
                                    {dictionary.forms.linkSelector.recentSearches}
                                </div>
                                {recentSearches.map((search, index) => (
                                    <div
                                        key={index}
                                        className="flex w-full items-center justify-between gap-3 border-l-2 border-transparent px-3 py-3 text-sm text-foreground transition-all hover:border-accent hover:bg-accent/5"
                                    >
                                        <button
                                            onClick={() => handleRecentClick(search)}
                                            className="flex items-center gap-2 flex-1 text-left"
                                        >
                                            <Clock className="h-4 w-4 text-primary-900" />
                                            <span>{search}</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                const updated = recentSearches.filter((s) => s !== search);
                                                setRecentSearches(updated);
                                                localStorage.setItem("recentSearches", JSON.stringify(updated));
                                            }}
                                            className="text-primary-600 hover:text-destructive transition-colors p-1"
                                            aria-label="حذف جستجو"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
